import {Grid} from "@material-ui/core";
import FormHelperText from "@material-ui/core/FormHelperText";
import InputAdornment from "@material-ui/core/InputAdornment";
import {ClearSharp} from "@material-ui/icons";
import * as PropTypes from "prop-types";
import React, {useEffect, useState} from "react";
import {BEAUFTRAGTER, BETREIBER, FACHADMIN, SYSTEMADMIN, ZUST_SBD} from "../../../consts/funktionaleRollen";
import {getMoeglicheAufnehmendeAnlagen} from "../../../services/AnlagenService";
import {getCurrentUser} from "../../../services/JwtService";
import {getPlanungMassnahmeEndpoint, getPlanungStatusEndpoint} from "../../../services/WertelistenService";
import {createCalculatedColumn, createColumn, createComponentColumn, createDateColumn} from "../../../utils/ColumnUtils";
import {useForm} from "../../../utils/FormUtils";
import {userHasAnyRole} from "../../../utils/SecurityUtils";
import {requiredDate, requiredString, requiredWerteListeObjekt} from "../../../utils/Validator";
import EAAccordion from "../../EAAccordion";
import EAButton from "../../EAButton";
import EAButtonBar from "../../EAButtonBar";
import EADialog from "../../EADialog";
import EATable from "../../EATable";
import EATextField from "../../EATextField";
import {logger} from "../../../services/LogService";
import EATableActionButton from "../../tableComponents/EATableActionButton";
import {useHttpSortById} from "../../../utils/usePreformattedHttp";
import {useConfirmation} from "../../ConfirmationDialog/ConfirmationDialogProvider";
import {confirmDeleteOption} from "../../ConfirmationDialog/confirmationDialogOptions";

function AnlageDialog(props) {
    let [{data, loading}] = useHttpSortById({
        url: getMoeglicheAufnehmendeAnlagen(props.betriebsId, props.anlagentyp),
        timeout: 600000,
    });
    const [filteredData, setFilteredData] = useState([]);
    const [filter, setFilter] = useState("");

    useEffect(() => {
        if (data) {
            setFilteredData([
                ...data.filter((item) => {
                    return !filter || item.betrieb.value.includes(filter.toLowerCase()) || item.bezeichnung.includes(filter.toLowerCase());
                }),
            ]);
        }
    }, [data, filter, loading]);

    return (
        <EADialog
            open={props.open}
            titleChildren={"Aufnehmende Anlage auswählen"}
            content={
                <div style={{width: "700px", maxHeight: "520px", minHeight: "400px"}}>
                    <div className="gridl" style={{gridTemplateColumns: "repeat(3,auto)", paddingBottom: "8px"}}>
                        <EATextField
                            value={filter}
                            onChange={(e) => {
                                e.preventDefault();
                                setFilter(e.target.value);
                            }}
                            id="aufnahmeFilter"
                            data-cy="aufnahmeFilter"
                            label="Suchkriterium"
                        />
                        <div>
                            <EAButton
                                onClick={() => {
                                    setFilter("");
                                }}
                                id="aufnahmeFilterSucheReset"
                                data-cy="aufnahmeFilterSucheReset"
                                label="Zurücksetzen"
                            />
                        </div>
                    </div>
                    <EATable
                        id="aufnahmeAuswahl"
                        initialSortColumn="betrieb"
                        columns={[
                            createComponentColumn(
                                "actions",
                                (row, index) => {
                                    return (
                                        <Grid container spacing={1} justify="center">
                                            <Grid item>
                                                <EATableActionButton id={"selectAufnahme-" + index} iconName="ico-check">
                                                    <span
                                                        onClick={() => {
                                                            setFilter("");
                                                            props.onClickOk(row);
                                                        }}
                                                        className={"w-100"}
                                                        style={{
                                                            height: "100%",
                                                            display: "block",
                                                        }}
                                                    />
                                                </EATableActionButton>
                                            </Grid>
                                        </Grid>
                                    );
                                },
                                "",
                                null,
                                {width: "50px", padding: "0"}
                            ),
                            createCalculatedColumn(
                                "betrieb",
                                (row) => {
                                    return row.betrieb.value;
                                },
                                "Betrieb"
                            ),
                            createColumn("bezeichnung", "Abwasseranlage"),
                        ]}
                        data={filteredData}
                        loading={loading}
                    />
                </div>
            }
            buttons={
                <EAButtonBar>
                    <EAButton id={"btnAbbrechen"} data-cy={"btnAbbrechen"} label={"Abbrechen"} onClick={props.onClickCancel}/>
                </EAButtonBar>
            }
        />
    );
}

AnlageDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClickCancel: PropTypes.func.isRequired,
    onClickOk: PropTypes.func.isRequired,
};

function TableDialog(props) {
    const [statusOptionen] = useHttpSortById(getPlanungStatusEndpoint);
    const [massnahmeOptionen] = useHttpSortById(getPlanungMassnahmeEndpoint);
    const [dlgOpen, setDlgOpen] = useState(false);

    function handleClose() {
        setDlgOpen(false);
    }

    function handleCloseOk(anlage) {
        setDlgOpen(false);
        const valuesToSet = {...props.data};
        valuesToSet.anschlussKA = anlage;
        props.setData(valuesToSet);
    }

    const getBezeichnerForKA = data => {
        if (data.anschlussKA && data.anschlussKA.bezeichnung) {
            return "anschlussKA.bezeichnung";
        } else if (data.anschlussKA) {
            return "anschlussKA.value";
        } else {
            return "";
        }
    }

    return (
        <React.Fragment>
            {dlgOpen &&
                <AnlageDialog
                    open={dlgOpen}
                    onClickCancel={handleClose}
                    onClickOk={handleCloseOk}
                    betriebsId={props.betriebsId}
                    anlagentyp={props.anlagentyp}
                />
            }
            <EADialog
                open={props.open}
                titleChildren={"Planung"}
                content={
                    <div style={{width: "600px"}}>
                        <div className="gridl" style={{gridTemplateColumns: "auto auto"}}>
                            {props.formFactory.renderAutocompleteWerteliste(
                                "status",
                                "status",
                                "Planungsstatus",
                                statusOptionen,
                                false,
                                false,
                                null
                            )}
                            {props.formFactory.renderAutocompleteWerteliste("massnahme", "massnahme", "Maßnahme", massnahmeOptionen)}
                            <div className="gridl" style={{gridColumnEnd: "span 2", gridTemplateColumns: "1fr auto"}}>
                                {props.formFactory.renderInput(
                                    getBezeichnerForKA({...props.data}),
                                    getBezeichnerForKA({...props.data}),
                                    "aufnehmende Kläranlage",
                                    "text",
                                    true,
                                    {
                                        InputProps: {
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <ClearSharp
                                                        onClick={() => {
                                                            const valuesToSet = {...props.data};
                                                            valuesToSet.anschlussKA = null;
                                                            return props.setData(valuesToSet);
                                                        }}
                                                    />
                                                </InputAdornment>
                                            ),
                                        },
                                    }
                                )}
                                <div>
                                    {props.formFactory.renderButton(
                                        "anschlussKAAendern",
                                        "anschlussKAAendern",
                                        "auswählen...",
                                        () => setDlgOpen(true),
                                        {
                                            "data-cy": "anschlussKAAendern",
                                        }
                                    )}
                                </div>
                            </div>
                            {props.formFactory.renderDatePicker("plandatum", "plandatum", "Umsetzungsdatum", false)}
                            <div/>
                            {props.formFactory.renderDatePicker("geaendertAm", "plandatum", "Geändert am", true)}
                            {props.formFactory.renderInput("geaendertVon.value", "geaendertVon.value", "Geändert von", "text", true)}
                            {props.formFactory.renderInput("beschreibung", "beschreibung", "Beschreibung", "text", false, {
                                multiline: true,
                                rows: 10,
                                style: {gridColumnEnd: "span 2"},
                            })}
                        </div>
                    </div>
                }
                buttons={
                    <EAButtonBar>
                        <EAButton id={"btnAbbrechen"} data-cy={"btnAbbrechen"} label={"Abbrechen"} onClick={props.onClickCancel}/>
                        <EAButton id={"btnOk"} data-cy={"btnOk"} color="primary" label={"Übernehmen"} onClick={props.onClickOk}/>
                    </EAButtonBar>
                }
                closeFunction={props.onClickCancel}
            />
        </React.Fragment>
    );
}

TableDialog.propTypes = {
    open: PropTypes.bool,
    formFactory: PropTypes.any,
    data: PropTypes.any,
    onClickCancel: PropTypes.func,
    onClickOk: PropTypes.func,
};

export default function Planungen(props) {
    const confirm = useConfirmation();
    const [extended, setExtended] = useState(true);
    const dataName = "planungen";
    const [tableIndex, setTableIndex] = useState(-1);
    const schema = {
        plandatum: requiredDate(),
        status: requiredWerteListeObjekt(),
        geaendertVon: {value: requiredString()},
    };
    const {values, setValues, errors, formFactory, validate, setErrors} = useForm({
        initialValues: {
            anschlussKA: {...props.anlage},
            beschreibung: "",
            massnahme: null,
            plandatum: null,
            status: null,
            geaendertAm: new Date(),
            geaendertVon: {value: getCurrentUser().sub},
        },
        onSubmit() {
        },
        schema,
    });
    const [dlgOpen, setDlgOpen] = useState(false);

    function handleDelete(index) {
        if (isDisabled()) {
            return;
        }

        confirm(confirmDeleteOption(
            "die Planung",
            () => removePlanung(index),
            () => {
            }
        ));
    }

    function removePlanung(index) {
        const tableData = [...props.data];
        tableData.splice(index, 1);
        setValues({});
        props.onChange(dataName, tableData);
    }

    function handleEdit(index) {
        logger.debug("Edit " + dataName);
        if (isDisabled()) {
            return;
        }
        setTableIndex(index);
        setValues(props.data[index]);
        setDlgOpen(true);
    }

    function handleClose() {
        setDlgOpen(false);
        setTableIndex(-1);
        setValues({anschlussKA: {...props.anlage}});
    }

    /**
     * Validierungsfehler zurücksetzten wenn Dialog geöffnet wird
     */
    useEffect(() => {
        setErrors({});
    }, [dlgOpen, setErrors])

    function handleCloseOk() {
        const e = validate();
        if (e && Object.keys(e).length > 0) {
            return;
        }
        setDlgOpen(false);
        const tableData = [...props.data];
        const planung = {...values};
        planung.geaendertVon = {value: getCurrentUser().sub};
        planung.geaendertAm = new Date();
        if (tableIndex === -1) {
            tableData.push(planung);
        } else {
            tableData[tableIndex] = {...tableData[tableIndex], ...planung};
        }
        logger.debug(dataName + "Close", tableData);
        setTableIndex(-1);
        setValues({anschlussKA: {...props.anlage}});
        props.onChange(dataName, tableData);
    }

    function isDisabled() {
        if (props.anlegen) return false;
        if (props.modus === "l") {
            return true;
        }
        if (props.modus === "s") {
            return !userHasAnyRole([BETREIBER, BEAUFTRAGTER, FACHADMIN, SYSTEMADMIN, ZUST_SBD]);
        }
        return true;
    }

    const getAKBezeichnung = row => {
        if (row.anschlussKA && row.anschlussKA.bezeichnung) {
            return row.anschlussKA.bezeichnung;
        } else if (row.anschlussKA && row.anschlussKA.value) {
            return row.anschlussKA.value;
        } else {
            return "";
        }
    }

    return (
        <React.Fragment>
            <TableDialog
                formFactory={formFactory}
                open={dlgOpen}
                onClickCancel={handleClose}
                data={values}
                errors={errors}
                setData={setValues}
                onClickOk={handleCloseOk}
                betriebsId={props.betriebsId}
                anlagentyp={props.anlagentyp}
            />
            <EAAccordion
                label={`Planungen (${props.data ? props.data.length : 0})`}
                className="gridl"
                id="planungenAccordion"
                variant="light"
                expanded={extended}
                onChange={(e, expanded) => setExtended(expanded)}
            >
                <EATable
                    id="planungen"
                    initialSortColumn="status"
                    columns={[
                        createComponentColumn(
                            "actions",
                            (row, index) => {
                                if (isDisabled()) return <div/>;
                                return (
                                    <Grid container spacing={1} justify="center">
                                        <Grid item>
                                            <EATableActionButton id={"editPlanung-" + index} iconName="ico-edit">
                                                <span
                                                    onClick={() => handleEdit(index)}
                                                    className={"w-100"}
                                                    style={{
                                                        height: "100%",
                                                        display: "block",
                                                    }}
                                                />
                                            </EATableActionButton>
                                        </Grid>
                                        <Grid item>
                                            <EATableActionButton id={"deletePlanung-" + index} iconName="ico-delete">
                                                <span
                                                    onClick={() => handleDelete(index)}
                                                    className={"w-100"}
                                                    style={{
                                                        height: "100%",
                                                        display: "block",
                                                    }}
                                                />
                                            </EATableActionButton>
                                        </Grid>
                                    </Grid>
                                );
                            },
                            "",
                            null,
                            {width: "50px", padding: "0"}
                        ),
                        createCalculatedColumn(
                            "status",
                            (row) => {
                                return row.status && row.status.value ? row.status.value : "";
                            },
                            "Planungsstatus"
                        ),
                        createCalculatedColumn(
                            "massnahme",
                            (row) => {
                                return row.massnahme && row.massnahme.value ? row.massnahme.value : "";
                            },
                            "Maßnahme",
                            {padding: "0"}
                        ),
                        createCalculatedColumn(
                            "anschlussKA",
                            getAKBezeichnung,
                            "Anschluss an KA",
                            {padding: "0"}
                        ),
                        createDateColumn("plandatum", "Umsetzungsdatum", {padding: "0"}),
                        createDateColumn("geaendertAm", "Geändert am", {padding: "0"}),
                        createCalculatedColumn(
                            "geaendertVon",
                            (row) => {
                                return row.geaendertVon && row.geaendertVon.value ? row.geaendertVon.value : "";
                            },
                            "Geändert von",
                            {padding: "0"}
                        ),
                    ]}
                    data={props.data}
                />
                {props.errors && props.errors.planungen && (
                    <FormHelperText id="planungen-helper-text" error>
                        {props.errors.planungen}
                    </FormHelperText>
                )}
                {userHasAnyRole([FACHADMIN, SYSTEMADMIN, BETREIBER, BEAUFTRAGTER, ZUST_SBD]) &&
                    props.formFactory.renderButton(
                        "addPlanung",
                        "addPlanung",
                        "Hinzufügen",
                        () => {
                            setTableIndex(-1);
                            setDlgOpen(true);
                        },
                        {
                            style: {placeSelf: "end"},
                        }
                    )}
            </EAAccordion>
        </React.Fragment>
    );
}

Planungen.propTypes = {
    data: PropTypes.any,
    errors: PropTypes.any,
    formFactory: PropTypes.any,
    onChange: PropTypes.func,
};
